import { apiClient } from "@/api/api-client";
import Combobox from "@/components/core/forms/combobox";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { Textarea } from "@/components/core/forms/textarea";
import { CategoryCombobox } from "@/components/leads/category-combobox";
import { StatusCombobox } from "@/components/leads/status-combobox";
import { leadCategoryListQueryOptions, leadStatusesQueryOptions, useLeadStatusCreateMutation } from "@/features/dashboard/api/queries/lead-query";
import leadService from "@/features/dashboard/api/services/lead-service";
import { type Lead, createLeadSchema } from "@/features/dashboard/types/lead";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

interface LeadFormProps {
	lead?: Lead;
	onSubmit: (values: z.infer<typeof createLeadSchema>) => Promise<void>;
	isSubmitting?: boolean;
}

async function loadUsers(inputValue: string) {
	const response = await apiClient.get("/user-approvals/all", { params: { search: inputValue } });
	const data = response.data;
	const users = data.data || data;
	return users.map((user: { id: string; first_name: string; last_name: string; email: string }) => ({
		value: user.id,
		label: `${user.first_name} ${user.last_name}`.trim() || user.email,
	}));
}

export function LeadForm({ lead, onSubmit }: LeadFormProps) {
	const statusesQuery = useQuery(leadStatusesQueryOptions());
	const [isGenerating, setIsGenerating] = useState(false);

	const form = useForm<z.infer<typeof createLeadSchema>>({
		resolver: zodResolver(createLeadSchema),
		defaultValues: lead
			? {
					reference_number: lead.reference_number,
					proposal_date: lead.proposal_date || "",
					project_description: lead.project_description,
					client_name: lead.client_name,
					amount: lead.amount,
					contact_person: lead.contact_person || "",
					contact_number: lead.contact_number || "",
					email: lead.email || "",
					client_address: lead.client_address || "",
					prepared_by: lead.prepared_by,
					status: lead.status,
					remarks: lead.remarks || "",
					request_date: lead.request_date ? lead.request_date.slice(0, 16) : "",
					response_date: lead.response_date ? lead.response_date.slice(0, 16) : "",
					category: lead.category,
					client_type: lead.client_type || "",
					priority: lead.priority || "",
					next_follow_up_date: lead.next_follow_up_date ? lead.next_follow_up_date.slice(0, 16) : "",
				}
			: {
					reference_number: "",
					proposal_date: "",
					project_description: "",
					client_name: "",
					amount: 0,
					contact_person: "",
					contact_number: "",
					email: "",
					client_address: "",
					prepared_by: "",
					status: "proposal_sent",
					remarks: "",
					request_date: "",
					response_date: "",
					category: "",
					client_type: "",
					priority: "",
					next_follow_up_date: "",
				},
	});

	const isNewLead = !lead;
	const proposalDate = form.watch("proposal_date");
	const category = form.watch("category");
	const clientType = form.watch("client_type");
	const initialRender = useRef(true);

	useEffect(() => {
		if (initialRender.current) {
			initialRender.current = false;
			return;
		}

		if (!proposalDate || !category || !clientType) {
			return;
		}

		let cancelled = false;
		setIsGenerating(true);
		leadService
			.generateReferenceNumber(proposalDate, category, clientType, lead?.id ?? undefined)
			.then((refNo) => {
				if (!cancelled) {
					form.setValue("reference_number", refNo);
				}
			})
			.catch(() => {})
			.finally(() => {
				if (!cancelled) {
					setIsGenerating(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [proposalDate, category, clientType]);

	return (
		<Form {...form}>
			<form id="lead-form" className="flex flex-col w-full gap-6" onSubmit={form.handleSubmit(onSubmit)}>
				<div className="flex flex-col w-full gap-6">
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="reference_number"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Proposal Ref No.</FormLabel>
										<FormGroup>
											<Input className="input" disabled placeholder={isGenerating ? "Generating..." : "Enter reference number"} {...field} readOnly={isNewLead} />
										</FormGroup>
										{isNewLead && (
											<p className="text-xs text-muted-foreground mt-1">Auto-generated from Proposal Date, Category, and Client Type.</p>
										)}
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-3">
							<FormField
								control={form.control}
								name="proposal_date"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Proposal Date</FormLabel>
										<FormGroup>
											<Input className="input" type="date" {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-3">
							<FormField
								control={form.control}
								name="client_type"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Client Type</FormLabel>
										<FormGroup>
											<select
												className="input flex h-[38px] w-full rounded-md border border-border bg-background px-3 py-2 text-base"
												value={field.value || ""}
												onChange={field.onChange}
											>
												<option value="">Select type...</option>
												<option value="PUB">Public (PUB)</option>
												<option value="PRI">Private (PRI)</option>
											</select>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-12">
							<FormField
								control={form.control}
								name="project_description"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Project Description</FormLabel>
										<FormGroup>
											<Textarea className="input" placeholder="Enter project description" rows={2} {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="client_name"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Client Name</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter client name" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="amount"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Amount</FormLabel>
										<FormGroup>
											<Input
												className="input"
												type="number"
												step="0.01"
												placeholder="0.00"
												{...field}
												onChange={(e) => field.onChange(Number.parseFloat(e.target.value) || 0)}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-4">
							<FormField
								control={form.control}
								name="contact_person"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Contact Person</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter contact person" {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-4">
							<FormField
								control={form.control}
								name="contact_number"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Contact No.</FormLabel>
										<FormGroup>
											<Input className="input" placeholder="Enter contact number" {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-4">
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Email</FormLabel>
										<FormGroup>
											<Input className="input" type="email" placeholder="Enter email" {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-12">
							<FormField
								control={form.control}
								name="client_address"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Client Address</FormLabel>
										<FormGroup>
											<Textarea className="input" placeholder="Enter client address" rows={2} {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="prepared_by"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Prepared By</FormLabel>
										<FormGroup>
											<Combobox
												placeholder="Select salesperson"
												loadOptions={loadUsers}
												defaultOptions
												clearable
												onValueChange={(value) => field.onChange(value)}
												defaultValue={field.value}
												placeholderValue={
													lead?.prepared_by_user
														? {
																value: lead.prepared_by_user.id,
																label: lead.prepared_by_user.name,
															}
														: undefined
												}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-3">
							<FormField
								control={form.control}
								name="status"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Status</FormLabel>
										<FormGroup>
											<StatusCombobox value={field.value} onChange={field.onChange} statuses={statusesQuery.data || []} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-3">
							<FormField
								control={form.control}
								name="category"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Category</FormLabel>
										<FormGroup>
											<CategoryCombobox value={field.value} onChange={field.onChange} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="priority"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Priority</FormLabel>
										<FormGroup>
											<select
												className="input flex h-[38px] w-full rounded-md border border-border bg-background px-3 py-2 text-base"
												value={field.value || ""}
												onChange={field.onChange}
											>
												<option value="">No priority</option>
												<option value="high">High</option>
												<option value="medium">Medium</option>
												<option value="low">Low</option>
											</select>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="next_follow_up_date"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Next Follow-up Date</FormLabel>
										<FormGroup>
											<Input className="input" type="datetime-local" {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

				<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="request_date"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Request Date/Time</FormLabel>
										<FormGroup>
											<Input className="input" type="datetime-local" {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="response_date"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Response Date/Time</FormLabel>
										<FormGroup>
											<Input className="input" type="datetime-local" {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-12">
							<FormField
								control={form.control}
								name="remarks"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Remarks</FormLabel>
										<FormGroup>
											<Textarea className="input" placeholder="Enter remarks" rows={3} {...field} value={field.value || ""} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
				</div>
			</form>
		</Form>
	);
}
